/**
 * terrain_normals — CONTINUOUS SHADING ACROSS TERRAIN LOD SEAMS (shader gaps round 6, agent J, 2026-09-30).
 *
 * WHY. Every Commons chunk took its normals from `BufferGeometry.computeVertexNormals()` over its OWN triangles.
 * Two things follow, and both draw a crease along chunk borders under a low sun (measured: `verify:terrain-normals`):
 *   1. A BORDER vertex sees only the triangles on its own side — its normal is ONE-SIDED, so two chunks of the same seg
 *      disagree on the very vertex they share (up to ~5° on seg-24 / seg-10 ground).
 *   2. The normal is the slope of the chunk's own facets — a seg-48 chunk (5.33 m cells) and a seg-24 one (10.67 m)
 *      average the height field over different spans, so even a perfect interior normal jumps at a 48 | 24 seam.
 * Skirts (`terrain_skirt.ts`) close the CRACK; they did nothing for the shading.
 *
 * THE FIX, two layers:
 *   A. ANALYTIC NORMALS AT A FIXED WORLD STEP. Every vertex takes the central difference of the conformed height field
 *      (`stampedHeight` — the same function the vertex heights come from) at ±`TERRAIN_NORMAL_STEP_M` in x and z,
 *      whatever the chunk's seg. The normal becomes a function of the WORLD POSITION alone, so a vertex two chunks share
 *      gets the same normal from both (to ~1e-5° — the samples are separately rounded coordinates of the same point).
 *      The step is the near ring's cell (256 / 48 = 5.33 m): a seg-48 chunk shades as its facets did (their 1-ring
 *      average IS a ±1-cell central difference — 0.03° apart on average), coarser rings shade with the near ring's
 *      detail (the "normals from the full-resolution height field, independent of the mesh LOD" of CDLOD / geometry
 *      clipmaps). Samples that land on a grid vertex reuse the chunk's own height.
 *   B. THE BORDER EXCHANGE for a seg MISMATCH. At a 48 | 24 seam every other fine vertex has no partner: the coarse
 *      side interpolates its two end vertices across that span while the fine side shades its own value in the middle —
 *      a T-junction in the normal AND in the per-vertex ground attributes. `stitchChunkEdges` writes, on the FINER
 *      chunk's side only, the coarse edge's own interpolation into the unpartnered vertices — exactly what the coarse
 *      triangles draw there (normalize(lerp) for the normal, lerp for the ground weights / tint / masks) — and restores
 *      the chunk's pristine edge when the neighbour is no longer coarser. A nested seam (48 | 24, 96 | 24, 96 | 48) is
 *      then EXACT; 24 | 10 share only the corners and the midpoint (24 : 10 is not an integer), so there the exchange
 *      reduces the step and cannot remove it. POSITIONS are never touched (the collider, `meshHeight` and the foliage
 *      keep the chunk's own grid): the T-junction's position step stays, hidden by the skirt — reported, not fixed.
 *
 * PURE except the geometry adapter at the bottom (which touches only `BufferGeometry` attributes). The worker, the sync
 * chunk path and the harness share it. Kill switches: `localStorage 'atlas:terrain-normals' = 'mesh'` (the old
 * `computeVertexNormals`, no exchange at all); `'atlas:terrain-seam-weights' = 'off'` (normals only — the ground
 * attributes keep their own T-junction). Lever: `__terrainNormals()`.
 */
import type * as THREE from 'three/webgpu';
import { CHUNK_SIZE, CHUNK_SEG_NEAR } from '../config.js';

/** The fixed world step of the central difference (m) — the near ring's cell. */
export const TERRAIN_NORMAL_STEP_M = CHUNK_SIZE / CHUNK_SEG_NEAR;

export type TerrainNormalsMode = 'analytic' | 'mesh';

function flag(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; }
}
/** `analytic` unless the kill switch says `mesh` (read at each chunk build). */
export function terrainNormalsMode(): TerrainNormalsMode { return flag('atlas:terrain-normals') === 'mesh' ? 'mesh' : 'analytic'; }
/** Whether the exchange also carries the ground attributes (default yes). */
export function seamWeightsOn(): boolean { return flag('atlas:terrain-seam-weights') !== 'off'; }

/** Runtime counters for the lever. */
export const terrainNormalStats = {
  analyticChunks: 0, meshChunks: 0, workerNormals: 0, syncNormals: 0,
  stitchedEdges: 0, restoredEdges: 0, stitchedVerts: 0, attrEdges: 0,
};

/** The height the chunk vertices were displaced by, at a world point (the conformed field). */
export type HeightAt = (x: number, z: number) => number;

/**
 * A chunk vertex's world coordinate — the worker's formula VERBATIM (`worldC + (col / seg) · size − size / 2`), so a
 * vertex two chunks share is the same double from both (col / seg is correctly rounded, and 2r/48 ≡ r/24).
 */
export function chunkVertexWorld(worldC: number, i: number, seg: number, size: number): number {
  return worldC + (i / seg) * size - size / 2;
}

/**
 * Normals of a (seg + 1)² chunk grid from the ANALYTIC height field: n ∝ (−∂h/∂x, 1, −∂h/∂z) by central differences at
 * ±`step` around each vertex. `heights` is the chunk's own grid (row-major, row = z); a sample that lands exactly on a
 * grid vertex (step a whole number of cells) reuses it, every other sample calls `heightAt` once (cached — a seg-24
 * chunk's half-cell samples are shared by two vertices). Returns `count × 3` floats and the extra samples it took.
 */
export function analyticChunkNormals(
  heightAt: HeightAt, heights: ArrayLike<number>, worldCX: number, worldCZ: number, size: number, seg: number,
  step: number = TERRAIN_NORMAL_STEP_M, out?: Float32Array,
): { normals: Float32Array; samples: number } {
  const side = seg + 1, count = side * side;
  const nrm = out ?? new Float32Array(count * 3);
  const cells = step * seg / size;                 // the step in this chunk's cells
  const K = Math.round(cells);
  const onGrid = K >= 1 && Math.abs(cells - K) < 1e-9;
  const cache = new Map<number, number>();
  let samples = 0;
  // Quantised to 1/8 m: two roundings of the SAME point share one sample; distinct sample points are ≥ 1 m apart.
  const keyOf = (x: number, z: number): number => (Math.round(x * 8) + 67108864) * 134217728 + (Math.round(z * 8) + 67108864);
  const direct = (x: number, z: number): number => {
    const k = keyOf(x, z);
    const hit = cache.get(k);
    if (hit !== undefined) return hit;
    const h = heightAt(x, z); samples++;
    cache.set(k, h);
    return h;
  };
  const inv2s = 1 / (2 * step);
  for (let row = 0; row < side; row++) {
    const z = chunkVertexWorld(worldCZ, row, seg, size);
    for (let col = 0; col < side; col++) {
      const x = chunkVertexWorld(worldCX, col, seg, size);
      const i = row * side + col;
      let hE: number, hW: number, hS: number, hN: number;
      if (onGrid) {
        hE = col + K <= seg ? heights[i + K] : direct(x + step, z);
        hW = col - K >= 0 ? heights[i - K] : direct(x - step, z);
        hS = row + K <= seg ? heights[i + K * side] : direct(x, z + step);
        hN = row - K >= 0 ? heights[i - K * side] : direct(x, z - step);
      } else {
        hE = direct(x + step, z); hW = direct(x - step, z);
        hS = direct(x, z + step); hN = direct(x, z - step);
      }
      const gx = (hE - hW) * inv2s, gz = (hS - hN) * inv2s;
      const l = Math.sqrt(gx * gx + 1 + gz * gz);
      nrm[i * 3] = -gx / l; nrm[i * 3 + 1] = 1 / l; nrm[i * 3 + 2] = -gz / l;
    }
  }
  return { normals: nrm, samples };
}

// ─── the border exchange (pure: channels over plain arrays or attributes) ───────────────────────────────────────

export type ChunkSide = 'north' | 'south' | 'west' | 'east';
export const SIDES: readonly ChunkSide[] = ['north', 'south', 'west', 'east'];
/** north = row 0 (min z), south = the last row (max z), west = col 0 (min x), east = the last col (max x). */
export const OPPOSITE: Readonly<Record<ChunkSide, ChunkSide>> = { north: 'south', south: 'north', west: 'east', east: 'west' };
/** The neighbour across a side, as a chunk-key offset. */
export const SIDE_OFFSET: Readonly<Record<ChunkSide, [number, number]>> = { north: [0, -1], south: [0, 1], west: [-1, 0], east: [1, 0] };

/** The vertex index of the `i`-th vertex along a side, in increasing world coordinate (x for north/south, z for west/east). */
export function edgeVertex(seg: number, side: ChunkSide, i: number): number {
  const n = seg + 1;
  return side === 'north' ? i : side === 'south' ? (n - 1) * n + i : side === 'west' ? i * n : i * n + (n - 1);
}
/** The skirt copy of that vertex (`addChunkSkirt` appends north, south, west, east after the grid), or −1 without a skirt. */
export function skirtVertex(seg: number, side: ChunkSide, i: number, vertexCount: number): number {
  const n = seg + 1;
  if (vertexCount !== n * n + 4 * n) return -1;
  const off = side === 'north' ? 0 : side === 'south' ? n : side === 'west' ? 2 * n : 3 * n;
  return n * n + off + i;
}

/** One per-vertex attribute as the exchange sees it. `unit` = a direction (the normal): interpolated as normalize(lerp). */
export interface Channel { itemSize: number; unit: boolean; get(v: number, c: number): number; set(v: number, c: number, x: number): void }
/** A channel over a flat array (the harness and the pure checks). */
export function arrayChannel(a: Float32Array | number[], itemSize: number, unit: boolean): Channel {
  return { itemSize, unit, get: (v, c) => a[v * itemSize + c], set: (v, c, x) => { a[v * itemSize + c] = x; } };
}

/**
 * The coarse edge's value at parameter t ∈ [0, 1] along it — what the coarse triangles DRAW there: the attribute is
 * interpolated linearly across the edge (three normalises the vertex normal in the vertex stage and interpolates unit
 * vectors, so a direction is normalize(lerp(n̂ⱼ, n̂ⱼ₊₁, f))). Returns whether t sits on a coarse vertex.
 */
export function coarseEdgeValueAt(coarse: Channel, coarseSeg: number, side: ChunkSide, t: number, out: number[]): boolean {
  const u = t * coarseSeg;
  const j = Math.min(coarseSeg - 1, Math.max(0, Math.floor(u + 1e-9)));
  const f = u - j;
  const a = edgeVertex(coarseSeg, side, j), b = edgeVertex(coarseSeg, side, j + 1);
  let l2 = 0;
  for (let c = 0; c < coarse.itemSize; c++) {
    const va = coarse.get(a, c), vb = coarse.get(b, c);
    out[c] = va + (vb - va) * f;
    l2 += out[c] * out[c];
  }
  if (coarse.unit) { const l = Math.sqrt(l2) || 1; for (let c = 0; c < coarse.itemSize; c++) out[c] /= l; }
  return f < 1e-9 || f > 1 - 1e-9;
}

/**
 * Write the coarse edge's interpolation into the FINE side's UNPARTNERED edge vertices (in place) and into their skirt
 * copies. A PARTNERED vertex (one both grids own) keeps its own value: its normal already agrees (the analytic field),
 * and a corner is shared by FOUR chunks — overwriting it from one coarse neighbour would break the seam with the
 * other (a seg-10 chunk's ground rules read a 32 m field where seg 24's read 8 m, so their corner WEIGHTS differ).
 * Returns how many vertices moved by more than 1e-6.
 */
export function stitchEdgeChannel(fine: Channel, fineSeg: number, fineSide: ChunkSide, coarse: Channel, coarseSeg: number, coarseSide: ChunkSide, fineVertexCount?: number): number {
  const o = new Array<number>(fine.itemSize).fill(0);
  let changed = 0;
  for (let i = 0; i <= fineSeg; i++) {
    if (coarseEdgeValueAt(coarse, coarseSeg, coarseSide, i / fineSeg, o)) continue; // partnered — see above
    const v = edgeVertex(fineSeg, fineSide, i);
    const s = fineVertexCount !== undefined ? skirtVertex(fineSeg, fineSide, i, fineVertexCount) : -1;
    let moved = false;
    for (let c = 0; c < fine.itemSize; c++) {
      if (Math.abs(fine.get(v, c) - o[c]) > 1e-6) moved = true;
      fine.set(v, c, o[c]);
      if (s >= 0) fine.set(s, c, o[c]);
    }
    if (moved) changed++;
  }
  return changed;
}

/** Copy a side's pristine edge (`snapshotEdges`) back into the live channel (and its skirt copies). */
export function restoreEdgeChannel(ch: Channel, seg: number, side: ChunkSide, pristine: ArrayLike<number>, vertexCount?: number): void {
  const k = ch.itemSize;
  for (let i = 0; i <= seg; i++) {
    const v = edgeVertex(seg, side, i);
    const s = vertexCount !== undefined ? skirtVertex(seg, side, i, vertexCount) : -1;
    for (let c = 0; c < k; c++) { ch.set(v, c, pristine[i * k + c]); if (s >= 0) ch.set(s, c, pristine[i * k + c]); }
  }
}

/** The four pristine edges of a channel, `(seg + 1) × itemSize` floats each. */
export function snapshotEdges(ch: Channel, seg: number): Record<ChunkSide, Float32Array> {
  const snap = {} as Record<ChunkSide, Float32Array>;
  for (const side of SIDES) {
    const e = new Float32Array((seg + 1) * ch.itemSize);
    for (let i = 0; i <= seg; i++) { const v = edgeVertex(seg, side, i); for (let c = 0; c < ch.itemSize; c++) e[i * ch.itemSize + c] = ch.get(v, c); }
    snap[side] = e;
  }
  return snap;
}

/** One chunk as the exchange sees it: its seg and, per attribute name, the live channel + its pristine edges. */
export interface SeamChunk { seg: number; vertexCount?: number; channels: Map<string, { ch: Channel; pristine: Record<ChunkSide, Float32Array>; dirty?: boolean }> }

/**
 * Settle ONE shared edge between chunk `a` (its side `sideA`) and its neighbour `b`, for every attribute both carry:
 * the finer side takes the coarser side's interpolation; equal segs both go back to their pristine edges (a stitch
 * left over from an earlier, coarser neighbour is undone). Marks the channels it wrote `dirty`.
 */
export function settleEdge(a: SeamChunk, sideA: ChunkSide, b: SeamChunk): 'a-stitched' | 'b-stitched' | 'restored' {
  const sideB = OPPOSITE[sideA];
  const verdict = a.seg > b.seg ? 'a-stitched' : b.seg > a.seg ? 'b-stitched' : 'restored';
  for (const [name, A] of a.channels) {
    const B = b.channels.get(name);
    if (!B || B.ch.itemSize !== A.ch.itemSize) continue;
    restoreEdgeChannel(A.ch, a.seg, sideA, A.pristine[sideA], a.vertexCount);
    restoreEdgeChannel(B.ch, b.seg, sideB, B.pristine[sideB], b.vertexCount);
    A.dirty = true; B.dirty = true;
    let moved = 0;
    if (verdict === 'a-stitched') moved = stitchEdgeChannel(A.ch, a.seg, sideA, B.ch, b.seg, sideB, a.vertexCount);
    else if (verdict === 'b-stitched') moved = stitchEdgeChannel(B.ch, b.seg, sideB, A.ch, a.seg, sideA, b.vertexCount);
    if (name === 'normal') terrainNormalStats.stitchedVerts += moved; else terrainNormalStats.attrEdges++;
  }
  if (verdict === 'restored') terrainNormalStats.restoredEdges++; else terrainNormalStats.stitchedEdges++;
  return verdict;
}

// ─── the geometry adapter (three attributes; EnvironmentModule calls these) ─────────────────────────────────────

/** The per-vertex SHADING attributes the exchange carries (never `position`): the normal, the v2 ground's five vec4s, the v1 ground's four. */
export const SEAM_ATTRIBUTES: readonly string[] = ['normal', 'gW0', 'gW1', 'gW2', 'gTint', 'gMask', 'biomeWA', 'biomeWB', 'roadW', 'fieldW'];

const SEAM_KEY = 'terrainSeam';
interface SeamState { seg: number; pristine: Map<string, Record<ChunkSide, Float32Array>> | null }

/**
 * Seat analytic normals on a freshly displaced chunk geometry (where `computeVertexNormals()` used to run — BEFORE the
 * cave cut and the skirt, so the skirt copies them) and mark the geometry for the exchange with its seg.
 */
export function seatAnalyticNormals(geo: THREE.BufferGeometry, normals: Float32Array, seg: number, NormalAttr: new (a: Float32Array, n: number) => THREE.BufferAttribute): void {
  geo.setAttribute('normal', new NormalAttr(normals, 3));
  (geo.userData as Record<string, unknown>)[SEAM_KEY] = { seg, pristine: null } satisfies SeamState;
  terrainNormalStats.analyticChunks++;
}

type AnyAttr = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
function attrChannel(attr: AnyAttr, unit: boolean): Channel {
  const inter = (attr as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute;
  if (inter) {
    const ia = attr as THREE.InterleavedBufferAttribute;
    const arr = ia.data.array as Float32Array, stride = ia.data.stride, off = ia.offset;
    return { itemSize: ia.itemSize, unit, get: (v, c) => arr[v * stride + off + c], set: (v, c, x) => { arr[v * stride + off + c] = x; } };
  }
  return arrayChannel((attr as THREE.BufferAttribute).array as Float32Array, attr.itemSize, unit);
}
function markDirty(attr: AnyAttr): void {
  const inter = (attr as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute;
  if (inter) (attr as THREE.InterleavedBufferAttribute).data.needsUpdate = true;
  else (attr as THREE.BufferAttribute).needsUpdate = true;
}

/** The exchange's view of a resident chunk geometry (the pristine edges are captured on first use — the geometry is final by then). */
function seamView(geo: THREE.BufferGeometry, withWeights: boolean): { chunk: SeamChunk; attrs: Map<string, AnyAttr> } | null {
  const st = (geo.userData as Record<string, unknown>)[SEAM_KEY] as SeamState | undefined;
  if (!st) return null;
  const attrs = new Map<string, AnyAttr>();
  for (const name of SEAM_ATTRIBUTES) {
    if (name !== 'normal' && !withWeights) continue;
    const a = geo.getAttribute(name) as AnyAttr | undefined;
    if (a && a.array instanceof Float32Array) attrs.set(name, a);
    else if (a && (a as THREE.InterleavedBufferAttribute).data?.array instanceof Float32Array) attrs.set(name, a);
  }
  if (!attrs.has('normal')) return null;
  const count = (geo.getAttribute('position') as THREE.BufferAttribute | undefined)?.count;
  if (!st.pristine) st.pristine = new Map();
  const channels: SeamChunk['channels'] = new Map();
  for (const [name, a] of attrs) {
    const ch = attrChannel(a, name === 'normal');
    let p = st.pristine.get(name);
    if (!p) { p = snapshotEdges(ch, st.seg); st.pristine.set(name, p); }
    channels.set(name, { ch, pristine: p });
  }
  return { chunk: { seg: st.seg, vertexCount: count, channels }, attrs };
}

/**
 * THE EXCHANGE around one chunk that was just added (a first build or a re-LOD swap): settle its four shared edges
 * against whatever neighbours are resident. `lookup(cx, cz)` returns a resident chunk's geometry (or null). Chunks
 * without analytic normals (the kill switch) are left alone. Returns the number of edges settled.
 */
export function stitchChunkEdges(cx: number, cz: number, lookup: (cx: number, cz: number) => THREE.BufferGeometry | null | undefined, withWeights: boolean = seamWeightsOn()): number {
  const me = lookup(cx, cz);
  const a = me ? seamView(me, withWeights) : null;
  if (!me || !a) return 0;
  let settled = 0;
  for (const side of SIDES) {
    const [dx, dz] = SIDE_OFFSET[side];
    const nb = lookup(cx + dx, cz + dz);
    const b = nb ? seamView(nb, withWeights) : null;
    if (!nb || !b) continue;
    settleEdge(a.chunk, side, b.chunk);
    for (const [name, v] of b.chunk.channels) if (v.dirty) markDirty(b.attrs.get(name)!);
    settled++;
  }
  for (const [name, v] of a.chunk.channels) if (v.dirty) markDirty(a.attrs.get(name)!);
  return settled;
}

// The lever (the page only — a worker has no `window`).
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__terrainNormals = () => ({
    mode: terrainNormalsMode(), weights: seamWeightsOn(), stepM: +TERRAIN_NORMAL_STEP_M.toFixed(4), ...terrainNormalStats,
  });
}
